import React, { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Phone, Send, Loader2, MapPin, Linkedin, Instagram, Youtube } from 'lucide-react';

const Kontakt = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.consent) {
      toast.error('Prosim potvrdte suhlas so spracovanim osobnych udajov');
      return;
    }

    setIsSubmitting(true);

    // Form submission is currently disabled (backend removed)
    setTimeout(() => {
      toast.info('Formular je momentalne v udrzbe. Prosim kontaktujte ma cez socialne siete.');
      setIsSubmitting(false);
    }, 500);
  };

  return (
    <div className="kontakt-page overflow-x-clip">
      {/* Hero */}
      <section className="relative bg-brand-dark-blue text-white section flex items-center overflow-x-clip">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(/symbol-3.svg)',
            backgroundSize: '400px 400px',
            backgroundRepeat: 'repeat'
          }}
        />

        <div className="container mx-auto px-4 sm:px-5 relative z-10 max-w-full min-w-0">
          <div className="max-w-4xl mx-auto text-center min-w-0 px-1">
            <div className="mb-8">
              <img
                src="/symbol-2.svg"
                alt="Peto Sloboda Symbol"
                className="mx-auto h-20 w-auto"
              />
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-heading font-bold mb-8 text-white break-words">
              Kontakt
            </h1>
            <p className="text-base sm:text-xl lg:text-2xl text-gray-300 leading-relaxed">
              Ozvite sa. Odpoviem osobne.
            </p>
          </div>
        </div>
      </section>

      {/* Kontakt info + Form */}
      <section className="section bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Kontaktne info */}
            <div>
              <h2 className="text-3xl font-heading font-bold mb-8 text-brand-dark-blue">
                Kontaktne informacie
              </h2>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-brand-orange bg-opacity-10 w-12 h-12 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <MapPin size={24} className="text-brand-orange" />
                  </div>
                  <div>
                    <p className="font-semibold text-brand-dark-blue mb-1">Miesto</p>
                    <p className="text-gray-700">Slovensko</p>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="text-2xl font-heading font-bold mb-6 text-brand-dark-blue">
                  Socialne siete
                </h3>
                <div className="flex space-x-4">
                  <a
                    href="https://www.linkedin.com/in/petosloboda/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-brand-dark-blue hover:bg-brand-orange text-white w-12 h-12 rounded-xl flex items-center justify-center transition-colors"
                  >
                    <Linkedin size={24} />
                  </a>
                  <a
                    href="https://www.instagram.com/petosloboda/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-brand-dark-blue hover:bg-brand-orange text-white w-12 h-12 rounded-xl flex items-center justify-center transition-colors"
                  >
                    <Instagram size={24} />
                  </a>
                  <a
                    href="https://www.youtube.com/@peto.sloboda"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-brand-dark-blue hover:bg-brand-orange text-white w-12 h-12 rounded-xl flex items-center justify-center transition-colors"
                  >
                    <Youtube size={24} />
                  </a>
                </div>
              </div>
            </div>

            {/* Formular */}
            <div className="bg-white rounded-3xl p-10 shadow-xl">
              <h2 className="text-3xl font-heading font-bold mb-8 text-brand-dark-blue">
                Napisat spravu
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-bold text-gray-700 mb-2">
                      Meno *
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all"
                      data-testid="kontakt-first-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="last_name" className="block text-sm font-bold text-gray-700 mb-2">
                      Priezvisko *
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all"
                      data-testid="kontakt-last-name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all"
                    data-testid="kontakt-email"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2">
                    Telefon (volitelne)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+421 915 123 456"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all"
                    data-testid="kontakt-phone"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-2">
                    Sprava *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Napistte, co chcete riesit..."
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent resize-none transition-all"
                    data-testid="kontakt-message"
                  />
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="consent"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleChange}
                    required
                    className="mt-1 mr-3 h-5 w-5 text-brand-orange border-gray-300 rounded focus:ring-brand-orange"
                    data-testid="kontakt-consent"
                  />
                  <label htmlFor="consent" className="text-sm text-gray-700">
                    Suhlasim so spracovanim osobnych udajov za ucelom kontaktovania. *
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-brand-orange to-red-500 hover:from-red-500 hover:to-brand-orange text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center"
                  data-testid="kontakt-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={24} className="mr-2 animate-spin" />
                      Odosiela sa...
                    </>
                  ) : (
                    <>
                      <Send size={24} className="mr-2" />
                      Odoslat spravu
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Kontakt;
